import React, { useState, useRef, useCallback } from 'react';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { useTranslation } from 'react-i18next';
import { RoutingControllerService } from '../api/services/RoutingControllerService';
import { useApiErrorHandler } from '../hooks/useApiErrorHandler';
import { useToast } from '../hooks/useToast';
import RoutingDeltaView, { type RoutingComparisonResult } from './RoutingDeltaView';
import type { RoutingDto } from '../api/models/RoutingDto';

export type ImportMode = 'CLONE' | 'OVERWRITE';

interface RoutingImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

const RoutingImportDialog: React.FC<RoutingImportDialogProps> = ({
  isOpen,
  onClose,
  onImportSuccess,
}) => {
  const { t } = useTranslation(['common']);
  const { handleError } = useApiErrorHandler();
  const { showToast } = useToast();
  
  const [currentStep, setCurrentStep] = useState<'upload' | 'preview' | 'delta' | 'importing'>('upload');
  const [importMode, setImportMode] = useState<ImportMode>('CLONE');
  const [importedRouting, setImportedRouting] = useState<RoutingDto | null>(null);
  const [comparisonResult, setComparisonResult] = useState<RoutingComparisonResult | null>(null);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const importModes = [
    { value: 'CLONE', text: t('routing.import.mode.clone', 'Klonen (neue UUIDs)') },
    { value: 'OVERWRITE', text: t('routing.import.mode.overwrite', 'Überschreiben oder Erstellen') },
  ];

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedFileName('');
      setImportedRouting(null);
      setIsReadingFile(false);
      return;
    }

    if (!file.name.endsWith('.json')) {
      showToast('error', t('common.error'), t('routing.import.error.invalidFile', 'Nur JSON-Dateien sind erlaubt'));
      setSelectedFileName('');
      setImportedRouting(null);
      setIsReadingFile(false);
      return;
    }

    setSelectedFileName(file.name);
    setIsReadingFile(true);
    setImportedRouting(null); // Reset before reading new file

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonContent = e.target?.result as string;
        if (!jsonContent) {
          throw new Error('File content is empty');
        }
        
        const parsed: RoutingDto = JSON.parse(jsonContent);
        // Validate basic structure
        if (!parsed.name) {
          throw new Error(t('routing.import.error.invalidStructure', 'Ungültige JSON-Struktur: Name fehlt'));
        }

        setImportedRouting(parsed);
        setIsReadingFile(false);
      } catch (error: any) {
        console.error('Error parsing JSON file:', error);
        handleError(error, 'parsing JSON file');
        showToast('error', t('common.error'), t('routing.import.error.parseError', 'Fehler beim Parsen der JSON-Datei'));
        setSelectedFileName('');
        setImportedRouting(null);
        setIsReadingFile(false);
      }
    };
    reader.onerror = (error) => {
      console.error('Error reading file:', error);
      showToast('error', t('common.error'), t('routing.import.error.readError', 'Fehler beim Lesen der Datei'));
      setSelectedFileName('');
      setImportedRouting(null);
      setIsReadingFile(false);
    };
    reader.readAsText(file);
  }, [handleError, showToast, t]);

  const handleContinueFromUpload = useCallback(() => {
    if (!importedRouting) {
      console.warn('No importedRouting, showing warning');
      showToast('warning', t('common.warning'), t('routing.import.warning.noFile', 'Bitte wählen Sie zuerst eine JSON-Datei aus.'));
      return;
    }
    setCurrentStep('preview');
  }, [importedRouting, showToast, t]);

  const handlePreview = useCallback(async () => {
    if (!importedRouting) return;

    setIsProcessing(true);
    try {
      // Call preview endpoint
      const comparison = await RoutingControllerService.previewImport(importedRouting);
      setComparisonResult(comparison);
      
      // Determine default import mode based on whether routing exists
      if (comparison.routingExists) {
        setImportMode('OVERWRITE');
      } else {
        setImportMode('CLONE');
      }
      
      setCurrentStep('delta');
    } catch (error) {
      handleError(error, 'previewing import');
    } finally {
      setIsProcessing(false);
    }
  }, [importedRouting, handleError]);

  const handleClose = useCallback(() => {
    setCurrentStep('upload');
    setImportedRouting(null);
    setComparisonResult(null);
    setConfirmOverwrite(false);
    setImportMode('CLONE');
    setSelectedFileName('');
    setIsReadingFile(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  }, [onClose]);

  const handleImport = useCallback(async () => {
    if (!importedRouting) return;

    // Validate overwrite confirmation
    if (importMode === 'OVERWRITE' && comparisonResult?.routingExists && !confirmOverwrite) {
      showToast('warning', t('common.warning'), t('routing.import.warning.confirmOverwrite', 'Bitte bestätigen Sie das Überschreiben'));
      return;
    }

    setIsProcessing(true);
    setCurrentStep('importing');

    try {
      await RoutingControllerService.importRouting(importedRouting, importMode, confirmOverwrite);

      showToast('success', t('common.success'), t('routing.import.success', 'Routing erfolgreich importiert'));
      handleClose();
      onImportSuccess();
    } catch (error: any) {
      handleError(error, 'importing routing');
      setCurrentStep('delta'); // Go back to delta view on error
    } finally {
      setIsProcessing(false);
    }
  }, [importedRouting, importMode, confirmOverwrite, comparisonResult, handleError, handleClose, showToast, t, onImportSuccess]);

  const handleBack = useCallback(() => {
    if (currentStep === 'delta') {
      setCurrentStep('preview');
    } else if (currentStep === 'preview') {
      setCurrentStep('upload');
    }
  }, [currentStep]);

  const getDialogTitle = (): string => {
    switch (currentStep) {
      case 'upload':
        return t('routing.import.title', 'Routing importieren');
      case 'preview':
        return t('routing.import.preview.title', 'Import-Vorschau');
      case 'delta':
        return t('routing.import.delta.title', 'Änderungen anzeigen');
      case 'importing':
        return t('routing.import.importing.title', 'Import läuft...');
      default:
        return t('routing.import.title', 'Routing importieren');
    }
  };

  const renderContent = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <div className="p-6">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('routing.import.selectFile', 'JSON-Datei auswählen')}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {selectedFileName && (
                <div className="mt-2 text-sm text-gray-600">
                  {t('routing.import.selectedFile', 'Ausgewählte Datei')}: <span className="font-medium">{selectedFileName}</span>
                </div>
              )}
            </div>
            <div className="text-sm text-gray-500">
              {t('routing.import.hint', 'Wählen Sie eine JSON-Datei aus, die ein Routing enthält.')}
            </div>
          </div>
        );

      case 'preview':
        return (
          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">
                {t('routing.import.preview.routingInfo', 'Routing-Informationen')}
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="space-y-2">
                  <div>
                    <span className="font-medium text-gray-700">{t('routing.name', 'Name')}:</span>{' '}
                    <span className="text-gray-900">{importedRouting?.name || '-'}</span>
                  </div>
                  {importedRouting?.description && (
                    <div>
                      <span className="font-medium text-gray-700">{t('routing.description', 'Beschreibung')}:</span>{' '}
                      <span className="text-gray-900">{importedRouting.description}</span>
                    </div>
                  )}
                  {importedRouting?.uuid && (
                    <div>
                      <span className="font-medium text-gray-700">UUID:</span>{' '}
                      <span className="text-gray-900 font-mono text-sm">{importedRouting.uuid}</span>
                    </div>
                  )}
                  {importedRouting?.operations && (
                    <div>
                      <span className="font-medium text-gray-700">{t('routing.operations', 'Operationen')}:</span>{' '}
                      <span className="text-gray-900">{importedRouting.operations.length}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('routing.import.mode.label', 'Import-Modus')}
              </label>
              <DropDownListComponent
                dataSource={importModes}
                fields={{ value: 'value', text: 'text' }}
                value={importMode}
                change={(e: any) => setImportMode(e.value as ImportMode)}
                cssClass="w-full"
              />
            </div>
            <div className="text-sm text-gray-500">
                    {importMode === 'CLONE' && t('routing.import.mode.cloneHint', 'Erstellt ein neues Routing mit neuen UUIDs. Der Name wird um " (Kopie)" ergänzt.')}
                    {importMode === 'OVERWRITE' && t('routing.import.mode.overwriteHint', 'Überschreibt ein bestehendes Routing, wenn die UUID übereinstimmt. Andernfalls wird ein neues Routing mit den UUIDs aus der JSON-Datei erstellt.')}
            </div>
          </div>
        );

      case 'delta':
        return (
          <div className="p-0" style={{ maxHeight: '80vh', overflow: 'auto' }}>
            {comparisonResult && (
              <>
                <RoutingDeltaView comparisonResult={comparisonResult} />
                {importMode === 'OVERWRITE' && comparisonResult.routingExists && (
                  <div className="p-6 border-t border-gray-200 bg-amber-50">
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="confirmOverwrite"
                        checked={confirmOverwrite}
                        onChange={(e) => setConfirmOverwrite(e.target.checked)}
                        className="mt-1"
                      />
                      <label htmlFor="confirmOverwrite" className="text-sm text-gray-700">
                        {t('routing.import.confirmOverwrite', 'Ich bestätige, dass ich das bestehende Routing überschreiben möchte. Alle Änderungen werden dauerhaft gespeichert.')}
                      </label>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 'importing':
        return (
          <div className="p-6 text-center">
            <div className="text-lg font-semibold mb-4">
              {t('routing.import.importing.message', 'Routing wird importiert...')}
            </div>
            <div className="text-sm text-gray-500">
              {t('routing.import.importing.hint', 'Bitte warten Sie, während das Routing importiert wird.')}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderFooter = useCallback(() => {
    switch (currentStep) {
      case 'upload':
        return (
          <div className="flex justify-end gap-2">
            <ButtonComponent
              type="button"
              onClick={handleClose}
              cssClass="e-outline"
            >
              {t('actions.cancel', 'Abbrechen')}
            </ButtonComponent>
            <ButtonComponent
              type="button"
              onClick={handleContinueFromUpload}
              cssClass="e-primary"
              disabled={!importedRouting || isProcessing || isReadingFile}
            >
              {isReadingFile ? t('routing.import.reading', 'Lese Datei...') : t('actions.next', 'Weiter')}
            </ButtonComponent>
          </div>
        );

      case 'preview':
        return (
          <div className="flex justify-end gap-2">
            <ButtonComponent
              type="button"
              onClick={handleBack}
              cssClass="e-outline"
              disabled={isProcessing}
            >
              {t('actions.back', 'Zurück')}
            </ButtonComponent>
            <ButtonComponent
              type="button"
              onClick={handlePreview}
              cssClass="e-primary"
              disabled={isProcessing}
            >
              {t('routing.import.preview', 'Vorschau anzeigen')}
            </ButtonComponent>
          </div>
        );

      case 'delta':
        return (
          <div className="flex justify-end gap-2">
            <ButtonComponent
              type="button"
              onClick={handleBack}
              cssClass="e-outline"
              disabled={isProcessing}
            >
              {t('actions.back', 'Zurück')}
            </ButtonComponent>
            <ButtonComponent
              type="button"
              onClick={handleImport}
              cssClass="e-primary"
              disabled={isProcessing || (importMode === 'OVERWRITE' && comparisonResult?.routingExists && !confirmOverwrite)}
            >
              {isProcessing ? t('actions.importing', 'Importiere...') : t('actions.import', 'Importieren')}
            </ButtonComponent>
          </div>
        );

      case 'importing':
        return null; // No buttons during import

      default:
        return null;
    }
  }, [currentStep, importedRouting, isProcessing, isReadingFile, importMode, comparisonResult, confirmOverwrite, handleClose, handleContinueFromUpload, handleBack, handlePreview, handleImport, t]);

  return (
    <DialogComponent
      width="90vw"
      height="90vh"
      isModal={true}
      visible={isOpen}
      close={handleClose}
      header={getDialogTitle()}
      showCloseIcon={true}
      cssClass="routing-import-dialog"
      footerTemplate={renderFooter}
    >
      {renderContent()}
    </DialogComponent>
  );
};

export default RoutingImportDialog;

